import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';

const MobileInstallPrompt = () => {
  const [show, setShow]               = useState(false);
  const [deferredPrompt, setDeferred] = useState(null);
  const [isIOS, setIsIOS]             = useState(false);
  const [visible, setVisible]         = useState(false);

  useEffect(() => {
    // Already installed as PWA → never show
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Previously dismissed this session → skip
    if (sessionStorage.getItem('svb-install-dismissed')) return;

    // iOS detection
    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Android / Chrome: wait for beforeinstallprompt
    const handleBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      const timer = setTimeout(() => { setShow(true); setVisible(true); }, 9000);
      return () => clearTimeout(timer);
    };
    window.addEventListener('beforeinstallprompt', handleBefore);

    // iOS: show tip after delay
    let iosTimer;
    if (ios) {
      iosTimer = setTimeout(() => { setShow(true); setVisible(true); }, 12000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBefore);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferred(null);
    }
    dismiss();
  };

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem('svb-install-dismissed', '1');
    setTimeout(() => setShow(false), 350);
  };

  if (!show) return null;

  return (
    <div
      className="fixed left-3 right-3 md:hidden"
      style={{
        zIndex: 10000,
        bottom: 'calc(76px + max(10px, env(safe-area-inset-bottom)) + 10px)',
        transform: visible ? 'translateY(0)' : 'translateY(120px)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
      }}
    >
      <div
        style={{
          background: 'rgba(12, 10, 24, 0.97)',
          border: '1px solid rgba(153,69,255,0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 18,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(153,69,255,0.1)',
        }}
      >
        {/* Logo */}
        {/* 17KB web asset instead of the 1.3MB SVG — shown at 40px */}
        <img
          src="/logos/SolVibe-Logo.web.png"
          alt="SOLVIBE"
          style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: '#ffffff', fontWeight: 700, fontSize: 13, lineHeight: 1.3, margin: 0 }}>
            Add SOLVIBE to Home Screen
          </p>
          <p style={{ color: '#6b7280', fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>
            {isIOS
              ? 'Tap Share ↑ → Add to Home Screen'
              : 'Install for faster, app-like experience'}
          </p>
        </div>

        {/* Install button (Android only) */}
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstall}
            style={{
              WebkitTapHighlightColor: 'transparent',
              flexShrink: 0,
              background: 'linear-gradient(135deg, #9945FF, #14F195)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Download size={13} />
            Install
          </button>
        )}

        {/* Dismiss */}
        <button
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          style={{
            WebkitTapHighlightColor: 'transparent',
            flexShrink: 0,
            background: 'transparent',
            border: 'none',
            color: '#4b5563',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default MobileInstallPrompt;
